import * as XLSX from 'xlsx';
import React from 'react';
import api from '../api';
// Use site-provided icons for Excel/PDF located in public/static/csv
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';

import { SCHOOL_PROFILE_SECTIONS } from '../data/schoolProfileFields';

// Utility: convert School Info to worksheet data
function schoolInfoToSheet(profile, SCHOOL_PROFILE_SECTIONS, valueFor, getConferenceName) {
    const rows = [];
    SCHOOL_PROFILE_SECTIONS.forEach(section => {
        if (section.title) rows.push([section.title]);
        section.fields.forEach(f => {
            if (f.name === 'school_logo') {
                const v = valueFor(profile, f.name);
                rows.push([f.label, v || '', v ? v : '']);
            } else if (f.name === 'athletic_conference_id') {
                const confId = valueFor(profile, f.name);
                rows.push([f.label, getConferenceName(confId)]);
            } else {
                rows.push([f.label, valueFor(profile, f.name)]);
            }
        });
        rows.push([]);
    });
    return rows;
}

function cigToSheet(cig, CIG_QUESTIONS, valueFor) {
    const rows = CIG_QUESTIONS.map((q) => {
        const key = q && q.dbField ? q.dbField : null;
        return [q.label, key ? valueFor(cig, key) : ''];
    });
    return rows;
}

function ilgToSheet(ilg, ILG_QUESTIONS, valueFor) {
    const rows = ILG_QUESTIONS.map((q) => {
        const key = q && q.dbField ? q.dbField : null;
        const raw = key ? valueFor(ilg, key) : '';
        const filesKey = q && q.filesDbField ? q.filesDbField : (q && q.dbField ? `${q.dbField}_files` : null);
        const filesRaw = filesKey ? valueFor(ilg, filesKey) : null;
        let files = [];
        try {
            files = typeof filesRaw === 'string' ? JSON.parse(filesRaw) : (Array.isArray(filesRaw) ? filesRaw : []);
        } catch (e) {
            files = [];
        }
        let fileUrls = '';
        if (Array.isArray(files) && files.length > 0) {
            fileUrls = files.map(f => f && (f.url || f.s3Url || f.location)).filter(Boolean).join(', ');
        }
        let answer = '';
        if (raw && String(raw).trim() && fileUrls) {
            answer = raw + ' | Files: ' + fileUrls;
        } else if (fileUrls) {
            answer = fileUrls;
        } else if (raw && String(raw).trim()) {
            answer = raw;
        }
        return [q.label, answer];
    });
    return rows;
}

function exportAllXLSX(profile, cig, ilg, SCHOOL_PROFILE_SECTIONS, CIG_QUESTIONS, ILG_QUESTIONS, valueFor, schoolName, getConferenceName) {
    const wb = XLSX.utils.book_new();
    const schoolInfoSheet = XLSX.utils.aoa_to_sheet(schoolInfoToSheet(profile, SCHOOL_PROFILE_SECTIONS, valueFor, getConferenceName));
    XLSX.utils.book_append_sheet(wb, schoolInfoSheet, 'School Info');
    const cigSheet = XLSX.utils.aoa_to_sheet(cigToSheet(cig, CIG_QUESTIONS, valueFor));
    XLSX.utils.book_append_sheet(wb, cigSheet, 'CIG Application');
    const ilgSheet = XLSX.utils.aoa_to_sheet(ilgToSheet(ilg, ILG_QUESTIONS, valueFor));
    XLSX.utils.book_append_sheet(wb, ilgSheet, 'ILG Application');
    XLSX.writeFile(wb, `${schoolName || 'school'}-all-data.xlsx`);
}

function exportSingleXLSX(sheetRows, sheetName, schoolName) {
    const wb = XLSX.utils.book_new();
    const sheet = XLSX.utils.aoa_to_sheet(sheetRows);
    XLSX.utils.book_append_sheet(wb, sheet, sheetName);
    XLSX.writeFile(wb, `${schoolName || 'school'}-${sheetName.replace(/\s+/g, '-')}.xlsx`);
}

const snakeToCamel = (s) => {
    return s.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());
};

const valueFor = (obj, key) => {
    if (!obj) return '';
    // direct match
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const v = obj[key];
        return v === null || v === undefined ? '' : String(v);
    }
    // try converting snake_case to camelCase (e.g., contact_first_name -> contactFirstName)
    const camel = snakeToCamel(key);
    if (Object.prototype.hasOwnProperty.call(obj, camel)) {
        const v = obj[camel];
        return v === null || v === undefined ? '' : String(v);
    }
    // also try lowercasing first char (in case of different capitalization)
    const alt = camel.charAt(0).toLowerCase() + camel.slice(1);
    if (Object.prototype.hasOwnProperty.call(obj, alt)) {
        const v = obj[alt];
        return v === null || v === undefined ? '' : String(v);
    }
    return '';
};

export default function SchoolProfileTemplate({ school, profile, cig, ilg, usersMap = {} }) {

    const navigate = useNavigate();
    const role = (typeof window !== 'undefined' && localStorage.getItem('adminRole')) || '';
    const isSuperAdmin = role && role.toUpperCase() === 'SUPER ADMIN';

    const [unlockOpen, setUnlockOpen] = React.useState(false);
    const [unlockFormKey, setUnlockFormKey] = React.useState('');
    const [unlockReason, setUnlockReason] = React.useState('');
    const [snack, setSnack] = React.useState({ open: false, message: '' });
    const [conferences, setConferences] = React.useState([]);
    const [previewOpen, setPreviewOpen] = React.useState(false);
    const [previewUrl, setPreviewUrl] = React.useState('');
    const [previewType, setPreviewType] = React.useState('image');
    const [previewTitle, setPreviewTitle] = React.useState('Preview');
    const [previewImageIndex, setPreviewImageIndex] = React.useState(-1);
    const [archiveOpen, setArchiveOpen] = React.useState(false);
    const [archiveInProgress, setArchiveInProgress] = React.useState(false);
    const [archiveResult, setArchiveResult] = React.useState(null);
    const [deleteOpen, setDeleteOpen] = React.useState(false);
    const [deleteConfirmName, setDeleteConfirmName] = React.useState('');
    const [deleteInProgress, setDeleteInProgress] = React.useState(false);
    const [deleteS3, setDeleteS3] = React.useState(false);
    const [pdfDownloading, setPdfDownloading] = React.useState({ cig: false, ilg: false });
    const [pdfPreparingMessage, setPdfPreparingMessage] = React.useState('');

    const openUnlock = (formKey) => {
        setUnlockFormKey(formKey);
        setUnlockReason('');
        setUnlockOpen(true);
    };

    const doUnlock = async () => {
        try {
            console.debug('admin unlock: schoolId=', school?.id, 'form=', unlockFormKey, 'reasonLen=', unlockReason ? unlockReason.length : 0);
            await api.post(`/admin/api/schools/${school.id}/unlock`, { form: unlockFormKey, reason: unlockReason });
            setSnack({ open: true, message: 'Form unlocked — page will refresh.' });
            setUnlockOpen(false);
            setTimeout(() => window.location.reload(), 700);
        } catch (e) {
            console.error(e);
            const j = e.response?.data || {};
            setSnack({ open: true, message: j.error || 'Unlock failed' });
        }
    };

    const openArchive = () => { setArchiveOpen(true); setArchiveResult(null); };

    const doArchive = async (includeS3) => {
        setArchiveInProgress(true);
        try {
            const res = await api.post(`/admin/api/schools/${school.id}/archive?includeS3=${includeS3}`);
            setArchiveResult(res.data || {});
            setSnack({ open: true, message: 'Archive created' });
        } catch (e) {
            console.error('Archive failed', e);
            const j = e.response?.data || {};
            setSnack({ open: true, message: j.error || 'Archive failed' });
        } finally {
            setArchiveInProgress(false);
        }
    };

    const openDelete = () => { setDeleteConfirmName(''); setDeleteOpen(true); };

    const isImageFileName = (name) => {
        const lc = String(name || '').toLowerCase();
        return lc.endsWith('.png') || lc.endsWith('.jpg') || lc.endsWith('.jpeg') || lc.endsWith('.gif') || lc.endsWith('.webp') || lc.endsWith('.svg') || lc.endsWith('.bmp') || lc.endsWith('.tiff') || lc.endsWith('.avif');
    };

    const isVideoFileName = (name) => {
        const lc = String(name || '').toLowerCase();
        return lc.endsWith('.mp4') || lc.endsWith('.mov') || lc.endsWith('.webm') || lc.endsWith('.m4v') || lc.endsWith('.ogv') || lc.endsWith('.ogg');
    };

    const openFilePreview = (url, type, title) => {
        setPreviewUrl(url);
        setPreviewType(type);
        setPreviewTitle(title || 'Preview');
        setPreviewImageIndex(-1);
        setPreviewOpen(true);
    };

    const closeFilePreview = () => {
        setPreviewOpen(false);
        setPreviewUrl('');
        setPreviewType('image');
        setPreviewTitle('Preview');
        setPreviewImageIndex(-1);
    };

    const doDelete = async () => {
        if (!archiveResult || !archiveResult.archiveId) {
            setSnack({ open: true, message: 'Archive required before delete' });
            return;
        }
        if (deleteConfirmName !== (school?.schoolName || '')) {
            setSnack({ open: true, message: 'School name does not match' });
            return;
        }
        setDeleteInProgress(true);
        try {
            await api.delete(`/admin/api/schools/${school.id}?archiveId=${archiveResult.archiveId}&deleteS3=${deleteS3}`);
            setSnack({ open: true, message: 'School deleted' });
            setTimeout(() => window.location.href = '/school-status', 800);
        } catch (e) {
            console.error('Delete failed', e);
            const j = e.response?.data || {};
            setSnack({ open: true, message: j.error || 'Delete failed' });
        } finally {
            setDeleteInProgress(false);
        }
    };


    const fmtDate = (d) => {
        if (!d) return '—';
        try {
            const t = new Date(d);
            if (isNaN(t.getTime())) return String(d);
            return t.toLocaleString();
        } catch (e) {
            return String(d);
        }
    };

    const pick = (obj, ...keys) => {
        if (!obj) return undefined;
        for (const k of keys) {
            if (Object.prototype.hasOwnProperty.call(obj, k) && obj[k] !== null && obj[k] !== undefined) return obj[k];
        }
        return undefined;
    };

    const auditFor = (obj) => {
        // try completed fields first
        const completed = pick(obj, 'completed', 'is_completed');
        let completedBy = pick(obj, 'completedBy', 'completed_by', 'completed_by_name', 'completedByName');
        // if completedBy is a numeric id, try to resolve to user name/email via usersMap
        if (completedBy && (typeof completedBy === 'number' || String(completedBy).match(/^\d+$/))) {
            const u = usersMap && usersMap[Number(completedBy)];
            if (u) completedBy = `${u.firstName || u.first_name || ''} ${u.lastName || u.last_name || ''} - ${u.email || ''}`.trim();
        }
        const completedAt = pick(obj, 'completedAt', 'completed_at', 'completed_at_ts');
        const lastSavedBy = pick(obj, 'lastSavedBy', 'last_saved_by', 'updated_by');
        const lastSavedAt = pick(obj, 'lastSavedAt', 'last_saved_at', 'updated_at', 'last_saved');

        if (completed) {
            return {
                status: 'completed',
                message: completedBy ? `Completed by ${completedBy} on ${fmtDate(completedAt)}` : `Completed on ${fmtDate(completedAt)}`,
            };
        }
        if (lastSavedBy || lastSavedAt) {
            let lastBy = lastSavedBy;
            if (lastBy && (typeof lastBy === 'number' || String(lastBy).match(/^\d+$/))) {
                const u2 = usersMap && usersMap[Number(lastBy)];
                if (u2) lastBy = `${u2.firstName || u2.first_name || ''} ${u2.lastName || u2.last_name || ''} - ${u2.email || ''}`.trim();
            }
            return {
                status: 'in_progress',
                message: lastBy ? `Last updated by ${lastBy} on ${fmtDate(lastSavedAt)}` : `Last updated ${fmtDate(lastSavedAt)}`,
            };
        }
        return { status: 'not_started', message: 'No auditing information available' };
    };

    const allSubmitted = Boolean(pick(profile, 'completed', 'is_completed') && pick(cig, 'completed', 'is_completed') && pick(ilg, 'completed', 'is_completed'));
    // local runtime question metadata state — initialize from local JS files, then try
    // to fetch server-provided labels and merge (preserve types/flags)
    const [cigQuestions, setCigQuestions] = React.useState([]);
    const [ilgQuestions, setIlgQuestions] = React.useState([]);

    // Create lookup map from conference ID to name
    const conferenceLookup = React.useMemo(() => {
        const map = new Map();
        conferences.forEach(c => {
            if (c && c.id) map.set(Number(c.id), c.name || '');
        });
        return map;
    }, [conferences]);

    const getConferenceName = (conferenceId) => {
        if (!conferenceId) return '';
        return conferenceLookup.get(Number(conferenceId)) || String(conferenceId);
    };

    React.useEffect(() => {
        let mounted = true;
        api.get('/api/public/questions/cig')
            .then((r) => {
                const arr = r && r.data;
                if (!mounted || !Array.isArray(arr)) return;
                const merged = arr.map((meta, idx) => ({
                    label: meta.label || '',
                    type: meta.type || 'text',
                    required: meta.required !== undefined ? meta.required : true,
                    hasFiles: meta.hasFiles !== undefined ? meta.hasFiles : false,
                    // prefer explicit dbField metadata from the server; fall back to common alternatives
                    dbField: meta.dbField || meta.db_field || meta.name || `cig_question_${idx + 1}`,
                    // preserve order if provided, otherwise default to index+1
                    order: (meta.order !== undefined && meta.order !== null) ? meta.order : (idx + 1),
                }));
                // ensure questions are consistently ordered by the metadata `order` field
                merged.sort((a, b) => (a.order || 0) - (b.order || 0));
                setCigQuestions(merged);
            })
            .catch(() => { });
        return () => { mounted = false; };
    }, []);

    React.useEffect(() => {
        let mounted = true;
        api.get('/api/public/athletic-conferences')
            .then((r) => {
                if (mounted) setConferences(r.data || []);
            })
            .catch(() => { });
        return () => { mounted = false; };
    }, []);

    React.useEffect(() => {
        let mounted = true;
        api.get('/api/public/questions/ilg')
            .then((r) => {
                const arr = r && r.data;
                if (!mounted || !Array.isArray(arr)) return;
                const merged = arr.map((meta, idx) => ({
                    label: meta.label || '',
                    type: meta.type || 'text',
                    required: meta.required !== undefined ? meta.required : true,
                    hasFiles: meta.hasFiles !== undefined ? meta.hasFiles : false,
                    dbField: meta.dbField || meta.db_field || meta.name || `ilg_question_${idx + 1}`,
                    filesDbField: meta.filesDbField || meta.files_db_field || meta.files_db || null,
                    legacyFilesDbField: meta.legacyFilesDbField || meta.legacy_files_db_field || null,
                    order: (meta.order !== undefined && meta.order !== null) ? meta.order : (idx + 1),
                }));
                // ensure questions are consistently ordered by the metadata `order` field
                merged.sort((a, b) => (a.order || 0) - (b.order || 0));
                setIlgQuestions(merged);
            })
            .catch(() => { });
        return () => { mounted = false; };
    }, []);

    // gather all ILG files (preserves question order)
    const getAllIlgFiles = () => {
        const out = [];
        ilgQuestions.forEach((q, idx) => {
            // Try all possible field names for files for this question
            // Only probe the explicit files field or the companion `_files` field of the descriptive dbField.
            const keysToTry = [];
            if (q && q.filesDbField) keysToTry.push(q.filesDbField);
            if (q && q.dbField) keysToTry.push(`${q.dbField}_files`);
            if (q && q.dbField) keysToTry.push(q.dbField);
            let arr = [];
            for (const key of keysToTry) {
                let s = valueFor(ilg, key);
                if (!s) continue;
                try {
                    arr = typeof s === 'string' ? JSON.parse(s) : (Array.isArray(s) ? s : []);
                } catch (e) {
                    arr = Array.isArray(s) ? s : [];
                }
                if (Array.isArray(arr) && arr.length > 0) break;
            }
            if (Array.isArray(arr)) {
                arr.forEach((f, i) => {
                    if (!f) return;
                    // support both string URL entries and object entries
                    const url = (typeof f === 'string') ? f : (f.url || f.s3Url || f.location);
                    if (!url) return;
                    const name = (typeof f === 'string') ? `file-${idx + 1}-${i}` : (f.name || f.filename || `file-${idx + 1}-${i}`);
                    out.push({ url, name, questionIndex: idx });
                });
            }
        });
        return out;
    };

    const allIlgFiles = getAllIlgFiles();
    const [presignedMap, setPresignedMap] = React.useState({});
    const imagePreviewItems = React.useMemo(() => {
        return allIlgFiles
            .filter((f) => f && isImageFileName(f.name))
            .map((f) => ({
                rawUrl: f.url,
                name: f.name,
                previewUrl: presignedMap[f.url] || '',
            }));
    }, [allIlgFiles, presignedMap]);

    const openImagePreview = (rawUrl, fallbackTitle) => {
        const idx = imagePreviewItems.findIndex((item) => item.rawUrl === rawUrl);
        if (idx === -1) {
            openFilePreview(presignedMap[rawUrl] || rawUrl, 'image', fallbackTitle || 'Preview');
            return;
        }
        const item = imagePreviewItems[idx];
        setPreviewImageIndex(idx);
        setPreviewType('image');
        setPreviewTitle(item.name || fallbackTitle || 'Preview');
        setPreviewUrl(item.previewUrl || '');
        setPreviewOpen(true);
    };

    const goToPrevImage = React.useCallback(() => {
        setPreviewImageIndex((current) => {
            if (current <= 0) return current;
            return current - 1;
        });
    }, []);

    const goToNextImage = React.useCallback(() => {
        setPreviewImageIndex((current) => {
            if (current < 0 || current >= imagePreviewItems.length - 1) return current;
            return current + 1;
        });
    }, [imagePreviewItems.length]);

    React.useEffect(() => {
        if (!previewOpen || previewType !== 'image') return;
        if (previewImageIndex < 0 || previewImageIndex >= imagePreviewItems.length) return;
        const item = imagePreviewItems[previewImageIndex];
        if (!item) return;
        setPreviewTitle(item.name || 'Preview');
        setPreviewUrl(item.previewUrl || '');
    }, [previewOpen, previewType, previewImageIndex, imagePreviewItems]);

    React.useEffect(() => {
        if (!previewOpen) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                closeFilePreview();
                return;
            }
            if (previewType !== 'image') return;
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                goToPrevImage();
            }
            if (e.key === 'ArrowRight') {
                e.preventDefault();
                goToNextImage();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [previewOpen, previewType, goToPrevImage, goToNextImage]);

    const downloadPdf = async (key) => {
        if (pdfDownloading[key]) return;

        const label = key === 'cig' ? 'CIG' : 'ILG';
        setPdfDownloading((prev) => ({ ...prev, [key]: true }));
        setPdfPreparingMessage(`Preparing ${label} PDF download. This may take some time for large files.`);

        try {
            const token = localStorage.getItem('token');
            // include school id when admin requests a PDF so server can return that school's data
            const url = `/api/public/${key}-applications/download${school && school.id ? `?schoolId=${encodeURIComponent(school.id)}` : ''}`;
            const res = await api.get(url, { responseType: 'blob', headers: token ? { Authorization: `Bearer ${token}` } : {} });
            const contentType = res.headers['content-type'] || 'application/pdf';
            const blob = new Blob([res.data], { type: contentType });
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            const FILE_NAME_MAP = {
                cig: '2026-Retool-Your-School-Campus-Improvement-Grant-Application.pdf',
                ilg: '2026-Retool-Your-School-Innovation-Lounge-Grant-Application.pdf'
            };
            a.download = FILE_NAME_MAP[key] || `${key}-application.${contentType.includes('pdf') ? 'pdf' : 'bin'}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (e) {
            console.error('PDF download failed', e);
            setSnack({ open: true, message: 'Failed to download PDF' });
        } finally {
            setPdfDownloading((prev) => ({ ...prev, [key]: false }));
            setPdfPreparingMessage('');
        }
    };

    React.useEffect(() => {
        let cancelled = false;
        allIlgFiles.forEach((f) => {
            if (!f || !f.url) return;
            if (presignedMap[f.url]) return;
            const fetchUrl = `/admin/api/s3/presign-get?url=${encodeURIComponent(f.url)}`;
            api.get(fetchUrl)
                .then((r) => {
                    const j = r.data;
                    if (cancelled) return;
                    if (j && j.url) {
                        setPresignedMap((p) => ({ ...p, [f.url]: j.url }));
                    }
                })
                .catch(() => { });
        });
        return () => { cancelled = true; };
    }, [allIlgFiles]);

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" component="h2">{school?.schoolName || 'School Profile'}</Typography>
                    <Typography variant="subtitle2" sx={{ mt: 0.5 }}>{school?.districtName || ''}</Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/school-status')}
                        sx={{ textTransform: 'none' }}
                    >
                        Back
                    </Button>
                    {isSuperAdmin && (
                        <>
                            <Button
                                variant="outlined"
                                size="small"
                                color="primary"
                                onClick={openArchive}
                                sx={{ textTransform: 'none' }}
                            >
                                Archive
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                color="error"
                                onClick={openDelete}
                                disabled={!archiveResult}
                                sx={{ textTransform: 'none' }}
                            >
                                Delete School
                            </Button>
                        </>
                    )}
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Box component="img" src="/static/csv/excel.png" sx={{ width: 20, height: 20 }} />}
                        onClick={() => exportAllXLSX(profile, cig, ilg, SCHOOL_PROFILE_SECTIONS, cigQuestions, ilgQuestions, valueFor, school?.schoolName, getConferenceName)}
                        sx={{ borderColor: '#d32f2f', color: '#d32f2f', fontWeight: 600 }}
                    >
                        Export All as XLSX
                    </Button>
                    {allSubmitted && <Chip label="All Applications Submitted" color="success" />}
                </Stack>
            </Box>

            {pdfPreparingMessage && (
                <Alert severity="info" sx={{ mt: 1 }}>
                    {pdfPreparingMessage}
                </Alert>
            )}


            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" component="h3">School Information</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                    label={auditFor(profile).status === 'completed' ? 'Completed' : auditFor(profile).status === 'in_progress' ? 'In Progress' : 'Not Started'}
                                    color={auditFor(profile).status === 'completed' ? 'success' : auditFor(profile).status === 'in_progress' ? 'warning' : 'default'}
                                    size="small"
                                />
                                <IconButton
                                    size="small"
                                    component="span"
                                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); exportSingleXLSX(schoolInfoToSheet(profile, SCHOOL_PROFILE_SECTIONS, valueFor, getConferenceName), 'School Info', school?.schoolName); }}
                                    title="Export School Info as XLSX"
                                >
                                    <Box component="img" src="/static/csv/excel.png" sx={{ width: 18, height: 18 }} />
                                </IconButton>
                                {auditFor(profile).status === 'completed' && (
                                    <IconButton size="small" component="span" onClick={(e) => { e.stopPropagation(); e.preventDefault(); openUnlock('SCHOOL_PROFILE'); }} title="Unlock School Profile">
                                        <LockOpenIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="body2">{auditFor(profile).message}</Typography>
                    </Box>
                    {SCHOOL_PROFILE_SECTIONS.map((section) => (
                        <Box key={section.key} sx={{ mb: 2 }}>
                            {section.title && (
                                <Chip label={section.title} sx={{ bgcolor: '#fb8c00', color: '#fff', mb: 1 }} />
                            )}
                            <Table size="small">
                                <TableBody>
                                    {section.fields.map((f) => (
                                        <TableRow key={f.name}>
                                            <TableCell sx={{ width: '35%', fontWeight: 600 }}>{f.label}</TableCell>
                                            <TableCell>
                                                {f.name === 'school_logo' ? (
                                                    (() => {
                                                        const v = valueFor(profile, f.name);
                                                        if (!v) return '—';
                                                        return (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                                                <Box component="img" src={v} alt="School logo" sx={{ width: 200, height: 200, objectFit: 'cover', border: '1px solid #e0e0e0' }} />
                                                                <Box sx={{ wordBreak: 'break-all' }}>
                                                                    <a href={v} target="_blank" rel="noopener noreferrer">{v}</a>
                                                                </Box>
                                                            </Box>
                                                        );
                                                    })()
                                                ) : f.name === 'athletic_conference_id' ? (
                                                    getConferenceName(valueFor(profile, f.name))
                                                ) : (
                                                    valueFor(profile, f.name)
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    ))}
                </AccordionDetails>
            </Accordion>
            {/* Unlock dialog */}
            <Dialog open={unlockOpen} onClose={() => setUnlockOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Unlock {unlockFormKey === 'CIG' ? 'CIG Application' : unlockFormKey === 'ILG' ? 'ILG Application' : 'School Profile'}</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 1 }}>This will mark the form as editable again for the school. Existing answers will be preserved. Please enter a short reason for the unlock (for audit purposes).</Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Reason"
                        type="text"
                        fullWidth
                        multiline
                        minRows={2}
                        value={unlockReason}
                        onChange={(e) => setUnlockReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUnlockOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={doUnlock} disabled={!unlockReason || unlockReason.trim().length < 3}>Unlock</Button>
                </DialogActions>
            </Dialog>

            <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={snack.open} autoHideDuration={4000} onClose={() => setSnack({ open: false, message: '' })}>
                <Alert onClose={() => setSnack({ open: false, message: '' })} severity="success" sx={{ width: '100%' }}>
                    {snack.message}
                </Alert>
            </Snackbar>

            <Dialog open={previewOpen} onClose={closeFilePreview} maxWidth="xl" fullWidth>
                <DialogTitle>
                    {previewType === 'image' && previewImageIndex >= 0 && imagePreviewItems.length > 0
                        ? `${previewTitle} (${previewImageIndex + 1} of ${imagePreviewItems.length})`
                        : previewTitle}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'background.paper', p: 2, position: 'relative' }}>
                    {previewType === 'video' ? (() => {
                        const isMov = /\.mov(\?|$)/i.test(previewTitle || '');
                        if (isMov) {
                            return (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="body1" sx={{ mb: 2 }}>
                                        MOV files cannot be previewed directly in this browser.
                                    </Typography>
                                    <Button variant="contained" component="a" href={previewUrl} target="_blank" rel="noopener noreferrer">
                                        Open / Download Video
                                    </Button>
                                </Box>
                            );
                        }
                        return (
                            <video
                                src={previewUrl}
                                controls
                                preload="metadata"
                                style={{ display: 'block', width: '100%', height: 'auto', maxHeight: '80vh', backgroundColor: '#000' }}
                            >
                                Your browser does not support video playback for this file.
                            </video>
                        );
                    })() : (
                        <>
                            {previewImageIndex >= 0 && imagePreviewItems.length > 0 && (
                                <>
                                    <IconButton
                                        aria-label="Previous image"
                                        onClick={goToPrevImage}
                                        disabled={previewImageIndex <= 0}
                                        sx={{
                                            position: 'absolute',
                                            left: 12,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            bgcolor: 'rgba(255,255,255,0.85)',
                                            border: '1px solid #ddd',
                                            '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
                                        }}
                                    >
                                        <ChevronLeftIcon />
                                    </IconButton>
                                    <IconButton
                                        aria-label="Next image"
                                        onClick={goToNextImage}
                                        disabled={previewImageIndex >= imagePreviewItems.length - 1}
                                        sx={{
                                            position: 'absolute',
                                            right: 12,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            bgcolor: 'rgba(255,255,255,0.85)',
                                            border: '1px solid #ddd',
                                            '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
                                        }}
                                    >
                                        <ChevronRightIcon />
                                    </IconButton>
                                </>
                            )}
                            {previewUrl ? (
                                <Box component="img" src={previewUrl} alt="Preview" sx={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain' }} />
                            ) : (
                                <Box sx={{ py: 4, px: 3, textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">Loading image…</Typography>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button component="a" href={previewUrl} target="_blank" rel="noopener noreferrer" sx={{ textTransform: 'none' }}>Open in new tab</Button>
                    <Button onClick={closeFilePreview}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Archive dialog */}
            <Dialog open={archiveOpen} onClose={() => setArchiveOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Create Archive for {school?.schoolName}</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 1 }}>Creating an archive will export the school's DB rows and optionally copy S3 objects into an archive prefix. This is required before deletion.</Typography>
                    {!archiveResult && (
                        <Box>
                            <Button variant="outlined" onClick={() => doArchive(false)} disabled={archiveInProgress} sx={{ mr: 1 }}>Create Archive (no S3)</Button>
                            <Button variant="contained" onClick={() => doArchive(true)} disabled={archiveInProgress}>Create Archive + Copy S3</Button>
                        </Box>
                    )}
                    {archiveInProgress && (<Typography sx={{ mt: 2 }}>Creating archive…</Typography>)}
                    {archiveResult && (
                        <Box sx={{ mt: 2 }}>
                            <Typography>Archive ID: {archiveResult.archiveId}</Typography>
                            {archiveResult.archiveJsonS3Key && (
                                <Typography>Archive JSON: <a href={`https://${archiveResult.archiveJsonS3Key ? `${process.env.REACT_APP_S3_HOST || ''}` : ''}/${archiveResult.archiveJsonS3Key}`} target="_blank" rel="noopener noreferrer">{archiveResult.archiveJsonS3Key}</a></Typography>
                            )}
                            {archiveResult.s3ArchivePrefix && (
                                <Typography>S3 Archive Prefix: {archiveResult.s3ArchivePrefix}</Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setArchiveOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Delete dialog */}
            <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Delete School {school?.schoolName}</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 1 }}>This will permanently delete the school and associated data from the database. You must provide the archive ID and type the school's name to confirm.</Typography>
                    <TextField label="Archive ID" fullWidth margin="dense" value={archiveResult?.archiveId || ''} disabled />
                    <TextField label="Type school name to confirm" fullWidth margin="dense" value={deleteConfirmName} onChange={(e) => setDeleteConfirmName(e.target.value)} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                        <input type="checkbox" checked={deleteS3} onChange={(e) => setDeleteS3(e.target.checked)} id="deleteS3" />
                        <label htmlFor="deleteS3">Also delete S3 objects for this school</label>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={doDelete} disabled={deleteInProgress || deleteConfirmName !== (school?.schoolName || '')}>Delete</Button>
                </DialogActions>
            </Dialog>

            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" component="h3">Campus Improvement Grant Application</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                    label={auditFor(cig).status === 'completed' ? 'Completed' : auditFor(cig).status === 'in_progress' ? 'In Progress' : 'Not Started'}
                                    color={auditFor(cig).status === 'completed' ? 'success' : auditFor(cig).status === 'in_progress' ? 'warning' : 'default'}
                                    size="small"
                                />
                                <IconButton
                                    size="small"
                                    component="span"
                                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); downloadPdf('cig'); }}
                                    title="Download CIG as PDF"
                                    disabled={pdfDownloading.cig}
                                >
                                    <Box component="img" src="/static/csv/pdf.png" sx={{ width: 18, height: 18 }} />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    component="span"
                                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); exportSingleXLSX(cigToSheet(cig, cigQuestions, valueFor), 'CIG Application', school?.schoolName); }}
                                    title="Export CIG as XLSX"
                                >
                                    <Box component="img" src="/static/csv/excel.png" sx={{ width: 18, height: 18 }} />
                                </IconButton>
                                {auditFor(cig).status === 'completed' && (
                                    <IconButton size="small" component="span" onClick={(e) => { e.stopPropagation(); e.preventDefault(); openUnlock('CIG'); }} title="Unlock CIG Application">
                                        <LockOpenIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="body2">{auditFor(cig).message}</Typography>
                    </Box>
                    <Table size="small">
                        <TableBody>
                            {cigQuestions.map((q, idx) => (
                                <TableRow key={`cig-${idx}`}>
                                    <TableCell sx={{ width: '35%', fontWeight: 600 }}>{q.label}</TableCell>
                                    <TableCell>{valueFor(cig, q && q.dbField ? q.dbField : `cig_question_${idx + 1}`)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </AccordionDetails>
            </Accordion>

            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" component="h3">Innovation Lounge Application</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                    label={auditFor(ilg).status === 'completed' ? 'Completed' : auditFor(ilg).status === 'in_progress' ? 'In Progress' : 'Not Started'}
                                    color={auditFor(ilg).status === 'completed' ? 'success' : auditFor(ilg).status === 'in_progress' ? 'warning' : 'default'}
                                    size="small"
                                />
                                <IconButton
                                    size="small"
                                    component="span"
                                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); downloadPdf('ilg'); }}
                                    title="Download ILG as PDF"
                                    disabled={pdfDownloading.ilg}
                                >
                                    <Box component="img" src="/static/csv/pdf.png" sx={{ width: 18, height: 18 }} />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    component="span"
                                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); exportSingleXLSX(ilgToSheet(ilg, ilgQuestions, valueFor), 'ILG Application', school?.schoolName); }}
                                    title="Export ILG as XLSX"
                                >
                                    <Box component="img" src="/static/csv/excel.png" sx={{ width: 18, height: 18 }} />
                                </IconButton>
                                {auditFor(ilg).status === 'completed' && (
                                    <IconButton size="small" component="span" onClick={(e) => { e.stopPropagation(); e.preventDefault(); openUnlock('ILG'); }} title="Unlock ILG Application">
                                        <LockOpenIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="body2">{auditFor(ilg).message}</Typography>
                    </Box>
                    <Table size="small">
                        <TableBody>
                            {ilgQuestions.map((q, idx) => {
                                const qNum = idx + 1;
                                const rows = [];
                                // Insert section headings to match form/PDF grouping
                                if (qNum === 1) {
                                    rows.push(
                                        <TableRow key={`ilg-heading-general`}>
                                            <TableCell colSpan={2} sx={{ bgcolor: '#f3f6fb', fontWeight: 700 }}>
                                                <Typography variant="subtitle1">General Information</Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                } else if (qNum === 4) {
                                    rows.push(
                                        <TableRow key={`ilg-heading-primary`}>
                                            <TableCell colSpan={2} sx={{ bgcolor: '#f3f6fb', fontWeight: 700 }}>
                                                <Typography variant="subtitle1">Primary Contact Information</Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                } else if (qNum === 10) {
                                    rows.push(
                                        <TableRow key={`ilg-heading-secondary`}>
                                            <TableCell colSpan={2} sx={{ bgcolor: '#f3f6fb', fontWeight: 700 }}>
                                                <Typography variant="subtitle1">Secondary Contact Information</Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                } else if (qNum === 16) {
                                    rows.push(
                                        <TableRow key={`ilg-heading-facilities`}>
                                            <TableCell colSpan={2} sx={{ bgcolor: '#f3f6fb', fontWeight: 700 }}>
                                                <Typography variant="subtitle1">Facilities Manager Contact Information</Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                } else if (qNum === 22) {
                                    rows.push(
                                        <TableRow key={`ilg-heading-space`}>
                                            <TableCell colSpan={2} sx={{ bgcolor: '#f3f6fb', fontWeight: 700 }}>
                                                <Typography variant="subtitle1">Information About Your Space</Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                } else if (qNum === 30) {
                                    rows.push(
                                        <TableRow key={`ilg-heading-project`}>
                                            <TableCell colSpan={2} sx={{ bgcolor: '#f3f6fb', fontWeight: 700 }}>
                                                <Typography variant="subtitle1">Information About Your Project</Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                }
                                const legacyKey = `ilg_question_${idx + 1}`;
                                const rawKey = q && q.dbField ? q.dbField : legacyKey;
                                const raw = valueFor(ilg, rawKey);
                                // determine files key preference only for questions that include files
                                let filesRaw = null;
                                if (q && (q.hasFiles === true || q.type === 'file')) {
                                    const filesKeyCandidate = q && q.filesDbField ? q.filesDbField : (q && q.dbField ? `${q.dbField}_files` : null);
                                    if (filesKeyCandidate) filesRaw = valueFor(ilg, filesKeyCandidate);
                                    if (!filesRaw && q && q.legacyFilesDbField) filesRaw = valueFor(ilg, q.legacyFilesDbField);
                                    // Also consider the descriptive dbField itself may contain the file JSON
                                    if (!filesRaw) filesRaw = raw;
                                    if (!filesRaw) filesRaw = valueFor(ilg, `${rawKey}_files`);
                                    if (!filesRaw) filesRaw = valueFor(ilg, `ilgQuestion${idx + 1}Files`);
                                    // fallback to old style where files may have been stored in the main question column
                                    if (!filesRaw) filesRaw = valueFor(ilg, `ilgQuestion${idx + 1}`);
                                    if (!filesRaw) filesRaw = valueFor(ilg, legacyKey);
                                }
                                let files = [];
                                try { files = typeof filesRaw === 'string' ? JSON.parse(filesRaw) : (Array.isArray(filesRaw) ? filesRaw : []); } catch (e) { files = []; }
                                // Only show the raw value if it is not a JSON string (i.e., not an array/object)
                                let showRaw = false;
                                if (raw && String(raw).trim() && (!Array.isArray(files) || files.length === 0)) {
                                    try {
                                        const parsed = JSON.parse(raw);
                                        if (typeof parsed !== 'object' || parsed === null) showRaw = true;
                                    } catch {
                                        showRaw = true;
                                    }
                                }
                                // For questions that include files, always show text above images
                                const showTextAboveImages = Boolean(q && q.hasFiles);
                                rows.push(
                                    <TableRow key={`ilg-${idx}`}>
                                        <TableCell sx={{ width: '35%', fontWeight: 600 }}>{q.label}</TableCell>
                                        <TableCell>
                                            {/* For Q24, always show text above images */}
                                            {showTextAboveImages && raw && String(raw).trim() && (
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography variant="body2">{raw}</Typography>
                                                </Box>
                                            )}
                                            {/* For other questions, show text only if not a file-list or JSON */}
                                            {!showTextAboveImages && showRaw ? raw : ''}
                                            {/* render thumbnails for this question's files */}
                                            {Array.isArray(files) && files.length > 0 && (
                                                <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', overflowX: 'auto', py: 1 }}>
                                                    {files.map((f, i) => {
                                                        // accept either string URL entries or file objects
                                                        const url = (typeof f === 'string') ? f : (f && (f.url || f.s3Url || f.location));
                                                        if (!url) return null;
                                                        const p = presignedMap[url];
                                                        const rawName = (typeof f === 'string') ? null : (f && (f.name || f.filename));
                                                        const inferredName = rawName || url.split('/').pop() || `file-${idx + 1}-${i}`;
                                                        const name = inferredName;
                                                        const isImage = isImageFileName(name);
                                                        const isVideo = isVideoFileName(name);
                                                        if (!p) return (
                                                            <Box key={`${url}-ph-${i}`} sx={{ width: 160, height: 120, bgcolor: '#f5f5f5', border: '1px solid #eee' }} />
                                                        );
                                                        if (isImage) {
                                                            return (
                                                                <Box
                                                                    key={`${url}-${i}`}
                                                                    component="img"
                                                                    src={p}
                                                                    alt={name}
                                                                    onClick={() => openImagePreview(url, name)}
                                                                    sx={{ height: 120, objectFit: 'cover', cursor: 'pointer', border: '1px solid #e0e0e0' }}
                                                                />
                                                            );
                                                        }
                                                        if (isVideo) {
                                                            return (
                                                                <Box key={`${url}-video-${i}`} sx={{ display: 'flex', alignItems: 'center', p: 1, border: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
                                                                    <Button variant="text" onClick={() => openFilePreview(p, 'video', name)} sx={{ textTransform: 'none', p: 0, minWidth: 0 }}>
                                                                        {name}
                                                                    </Button>
                                                                </Box>
                                                            );
                                                        }
                                                        // non-image: render a simple link with filename
                                                        return (
                                                            <Box key={`${url}-link-${i}`} sx={{ display: 'flex', alignItems: 'center', p: 1, border: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
                                                                <a href={p} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#1976d2' }}>{name}</a>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                                return rows;
                            })}
                        </TableBody>
                    </Table>
                    {/* Removed allIlgFiles gallery to avoid duplicate images at the bottom */}
                </AccordionDetails>
            </Accordion>
        </Box>
    );
}

// Unlock dialog markup (placed after component to keep main render concise)
/* Note: Dialogs are rendered via React state inside the component above. */
